/**
 * Next.js client instrumentation entry point.
 *
 * Runs before any other client code on each page load. We use it to install a
 * one-shot monkey-patch on `globalThis.fetch` that records the X-Request-ID
 * header from every response into a ring buffer. The buffer is consumed by
 * RequestIdBadge (in the UI) and by toast helpers (which attach the active
 * ID to error notifications).
 *
 * Design notes:
 * - The patch is idempotent. If hot-reload re-runs this module the original
 *   fetch is preserved on a sentinel property; the wrapper is only installed
 *   once per realm.
 * - The wrapper passes the Response object through untouched. Header reads
 *   are safe; we never consume the body.
 * - All work happens behind a try/catch. Buffer write failures cannot break
 *   the fetch call — the user's request still resolves with the unmodified
 *   Response.
 * - Gated by NEXT_PUBLIC_DEBUG_REQUEST_IDS during the rollout. Set the flag
 *   in `.env.local` (dev) or as a build-time env var (staging) to enable.
 *   The flag is read at build time; flipping it requires a rebuild.
 *
 * This addresses the WS-2 ask in feat/primitives-observability — closes the
 * browser-to-server correlation gap so bug reports carry a request_id that
 * matches the ai-service log line that ran the request.
 */

import { pushRequestId } from "@/lib/request-id-buffer";

const PATCH_SENTINEL = Symbol.for("researchos.requestIdFetchPatched");

interface PatchedGlobal {
  fetch: typeof globalThis.fetch;
  [PATCH_SENTINEL]?: true;
}

export function register() {
  if (typeof window === "undefined") return;
  if (process.env.NEXT_PUBLIC_DEBUG_REQUEST_IDS !== "1") return;

  const g = globalThis as unknown as PatchedGlobal;
  if (g[PATCH_SENTINEL]) return;

  const originalFetch: typeof globalThis.fetch = g.fetch.bind(globalThis);

  g.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    let response: Response;
    try {
      response = await originalFetch(input, init);
    } catch (err) {
      // Re-throw the original error untouched. The user's request failed
      // before any response came back; nothing to record.
      throw err;
    }

    try {
      const rid = response.headers.get("x-request-id");
      if (rid) {
        const url =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.toString()
              : input.url;
        pushRequestId({
          rid,
          url,
          status: response.status,
          ts: Date.now(),
        });
      }
    } catch {
      // Buffer write failure must never affect the response path. The caller
      // still gets the un-tampered Response below.
    }

    return response;
  };

  g[PATCH_SENTINEL] = true;
}
